import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Download, ChevronDown, ChevronUp, ExternalLink, ChevronLeft, ChevronRight, X, FileText, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

function isImageFile(name) {
  if (!name) return false;
  return imageExtensions.some(ext => name.toLowerCase().endsWith(ext));
}

function isPDFFile(name) {
  if (!name) return false;
  return name.toLowerCase().endsWith('.pdf');
}

function isHTMLFile(name) {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.endsWith('.html') || n.endsWith('.htm');
}

function getFileColor(name) {
  if (!name) return { bg: 'bg-slate-100', text: 'text-slate-600', label: 'FILE' };
  const n = name.toLowerCase();
  if (imageExtensions.some(e => n.endsWith(e))) return { bg: 'bg-purple-50', text: 'text-purple-600', label: 'IMG' };
  if (n.endsWith('.pdf')) return { bg: 'bg-red-50', text: 'text-red-600', label: 'PDF' };
  if (n.endsWith('.doc') || n.endsWith('.docx')) return { bg: 'bg-blue-50', text: 'text-blue-600', label: 'DOC' };
  if (n.endsWith('.xls') || n.endsWith('.xlsx')) return { bg: 'bg-green-50', text: 'text-green-600', label: 'XLS' };
  if (n.endsWith('.ppt') || n.endsWith('.pptx')) return { bg: 'bg-orange-50', text: 'text-orange-600', label: 'PPT' };
  if (n.endsWith('.html') || n.endsWith('.htm')) return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'HTML' };
  if (n.endsWith('.zip')) return { bg: 'bg-yellow-50', text: 'text-yellow-600', label: 'ZIP' };
  return { bg: 'bg-slate-100', text: 'text-slate-600', label: n.split('.').pop()?.toUpperCase() || 'FILE' };
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

// Image Carousel Component for Lessons
function LessonImageCarousel({ images, lessonId, token, onDownload }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [fullscreen, setFullscreen] = useState(false);

  const loadImage = async (index) => {
    if (loadedImages[index]) return;
    try {
      const response = await axios.get(
        `${API_URL}/lessons/download/${lessonId}?file_index=${images[index].originalIndex}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 15000 }
      );
      const url = URL.createObjectURL(response.data);
      setLoadedImages(prev => ({ ...prev, [index]: url }));
    } catch (e) {
      console.error('Failed to load image:', e);
    }
  };

  useEffect(() => {
    loadImage(currentIndex);
    if (currentIndex > 0) loadImage(currentIndex - 1);
    if (currentIndex < images.length - 1) loadImage(currentIndex + 1);
  }, [currentIndex]);

  const goNext = () => setCurrentIndex(i => Math.min(i + 1, images.length - 1));
  const goPrev = () => setCurrentIndex(i => Math.max(i - 1, 0));

  if (images.length === 0) return null;

  // Single image
  if (images.length === 1) {
    return (
      <div className="space-y-2">
        <div className="relative rounded-lg overflow-hidden bg-slate-100">
          {loadedImages[0] ? (
            <img
              src={loadedImages[0]}
              alt={images[0].name}
              className="w-full h-auto max-h-72 object-contain cursor-pointer"
              onClick={() => setFullscreen(true)}
            />
          ) : (
            <div className="w-full h-48 flex items-center justify-center">
              <span className="text-sm text-slate-400">Зарежда...</span>
            </div>
          )}
        </div>
        <Button onClick={() => onDownload(images[0].originalIndex, images[0].name)} variant="outline" size="sm" className="text-xs">
          <Download className="h-3 w-3 mr-1" /> {images[0].name}
        </Button>
        
        {fullscreen && loadedImages[0] && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setFullscreen(false)}>
            <button className="absolute top-4 right-4 text-white p-2" onClick={() => setFullscreen(false)}>
              <X className="h-6 w-6" />
            </button>
            <img src={loadedImages[0]} alt={images[0].name} className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    );
  }

  // Multiple images - carousel
  return (
    <div className="space-y-2">
      <div className="relative rounded-lg overflow-hidden bg-slate-100">
        <div className="relative aspect-video flex items-center justify-center min-h-[200px]">
          {loadedImages[currentIndex] ? (
            <img
              src={loadedImages[currentIndex]}
              alt={images[currentIndex].name}
              className="max-w-full max-h-full object-contain cursor-pointer"
              onClick={() => setFullscreen(true)}
            />
          ) : (
            <span className="text-sm text-slate-400">Зарежда...</span>
          )}
        </div>

        {currentIndex > 0 && (
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {currentIndex < images.length - 1 && (
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((img, i) => (
            <button
              key={img.path || `dot-${i}`}
              onClick={() => setCurrentIndex(i)}
              className={`w-2 h-2 rounded-full ${i === currentIndex ? 'bg-white' : 'bg-white/50'}`}
            />
          ))}
        </div>

        <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      <Button onClick={() => onDownload(images[currentIndex].originalIndex, images[currentIndex].name)} variant="outline" size="sm" className="text-xs">
        <Download className="h-3 w-3 mr-1" /> {images[currentIndex].name}
      </Button>

      {fullscreen && loadedImages[currentIndex] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setFullscreen(false)}>
          <button className="absolute top-4 right-4 text-white p-2 z-10" onClick={() => setFullscreen(false)}>
            <X className="h-6 w-6" />
          </button>
          {currentIndex > 0 && (
            <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-4 text-white p-2">
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-4 text-white p-2">
              <ChevronRight className="h-8 w-8" />
            </button>
          )}
          <img src={loadedImages[currentIndex]} alt={images[currentIndex].name} className="max-w-full max-h-full object-contain p-4" />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}

// PDF Viewer with Android fallback
function AuthLessonPDF({ lessonId, fileIndex, token, fileName }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const android = isAndroid();

  const loadPDF = async () => {
    if (blobUrl) { setExpanded(true); return; }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/lessons/download/${lessonId}?file_index=${fileIndex}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'arraybuffer', timeout: 30000 }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setBlobUrl(url);
      setExpanded(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error('Файлът не е намерен.');
      } else if (status === 403) {
        toast.error('Нямате достъп до този файл.');
      } else {
        toast.error('Грешка при зареждане на PDF.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (expanded) { setExpanded(false); } else { loadPDF(); }
  };

  const openInNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  };

  return (
    <div className="border border-red-200 rounded-md overflow-hidden">
      <button
        onClick={handleToggle}
        data-testid="pdf-preview-toggle"
        className="w-full flex items-center justify-between px-3 py-2 bg-red-50 hover:bg-red-100 transition-colors text-sm"
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-red-600 text-xs bg-red-100 px-1.5 py-0.5 rounded">PDF</span>
          <span className="text-slate-700 truncate max-w-[200px]">{fileName}</span>
          {loading && <span className="text-xs text-slate-400 ml-1">Зарежда...</span>}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {expanded && blobUrl && (
        <div className="w-full bg-slate-50 border-t border-red-200">
          {android ? (
            <div className="p-4 text-center space-y-3">
              <FileText className="h-12 w-12 text-red-400 mx-auto" />
              <p className="text-sm text-slate-600">
                PDF прегледът не е наличен на Android браузър.
              </p>
              <Button onClick={openInNewTab} className="bg-red-600 hover:bg-red-700 text-white">
                <ExternalLink className="h-4 w-4 mr-2" />
                Отвори PDF
              </Button>
            </div>
          ) : (
            <>
              <iframe
                src={blobUrl}
                title={fileName}
                className="w-full border-0"
                style={{ height: '500px', display: 'block' }}
              />
              <div className="flex gap-2 px-3 py-2 border-t border-red-100">
                <a href={blobUrl} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-red-600 underline flex items-center gap-1 hover:text-red-800">
                  <ExternalLink className="h-3 w-3" /> Отвори в нов таб
                </a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Interactive HTML presentation viewer — works on laptop, Android & iOS via srcdoc + sandboxed iframe
function AuthLessonHTML({ lessonId, fileIndex, token, fileName }) {
  const [html, setHtml] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const loadHTML = async () => {
    if (html !== null) { setExpanded(true); return; }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/lessons/download/${lessonId}?file_index=${fileIndex}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'text', timeout: 30000 }
      );
      const text = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      setHtml(text);
      setExpanded(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error('Файлът не е намерен.');
      else if (status === 403) toast.error('Нямате достъп до този файл.');
      else toast.error('Грешка при зареждане на презентацията.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (expanded) setExpanded(false);
    else loadHTML();
  };

  const sandbox = 'allow-scripts allow-popups allow-forms allow-modals allow-popups-to-escape-sandbox';

  return (
    <div className="border border-amber-200 rounded-md overflow-hidden">
      <button
        onClick={handleToggle}
        data-testid="html-preview-toggle"
        className="w-full flex items-center justify-between px-3 py-2 bg-amber-50 hover:bg-amber-100 transition-colors text-sm"
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-amber-600 text-xs bg-amber-100 px-1.5 py-0.5 rounded">HTML</span>
          <span className="text-slate-700 truncate max-w-[200px]">{fileName}</span>
          {loading && <span className="text-xs text-slate-400 ml-1">Зарежда...</span>}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && html !== null && (
        <div className="w-full bg-white border-t border-amber-200">
          <iframe
            srcDoc={html}
            title={fileName}
            sandbox={sandbox}
            className="w-full border-0"
            style={{ height: '500px', display: 'block' }}
            data-testid="html-preview-iframe"
          />
          <div className="flex gap-3 px-3 py-2 border-t border-amber-100">
            <button
              onClick={() => setFullscreen(true)}
              data-testid="html-fullscreen-btn"
              className="text-xs text-amber-700 underline flex items-center gap-1 hover:text-amber-900"
            >
              <Maximize2 className="h-3 w-3" /> Цял екран
            </button>
          </div>
        </div>
      )}

      {fullscreen && html !== null && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="html-fullscreen-overlay">
          <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200">
            <span className="text-sm font-medium text-slate-700 truncate">{fileName}</span>
            <button onClick={() => setFullscreen(false)} className="text-slate-500 hover:text-red-500 p-1" data-testid="html-fullscreen-close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <iframe
            srcDoc={html}
            title={`${fileName} - цял екран`}
            sandbox={sandbox}
            className="flex-1 w-full border-0"
          />
        </div>
      )}
    </div>
  );
}

export default function LessonFiles({ lesson, token }) {
  const files = (lesson.file_paths && lesson.file_paths.length > 0)
    ? lesson.file_paths.map((fp, i) => ({ path: fp, name: lesson.file_names?.[i] || 'file', originalIndex: i }))
    : (lesson.file_path ? [{ path: lesson.file_path, name: lesson.file_name || 'file', originalIndex: 0 }] : []);

  if (files.length === 0) return null;

  const imageFiles = files.filter(f => isImageFile(f.name));
  const pdfFiles = files.filter(f => isPDFFile(f.name));
  const htmlFiles = files.filter(f => isHTMLFile(f.name));
  const otherFiles = files.filter(f => !isImageFile(f.name) && !isPDFFile(f.name) && !isHTMLFile(f.name));

  const handleDownload = async (fileIndex, fileName) => {
    try {
      const response = await axios.get(
        `${API_URL}/lessons/download/${lesson.lesson_id}?file_index=${fileIndex}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Файлът е изтеглен');
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) toast.error('Файлът не е намерен.');
      else toast.error('Грешка при изтегляне.');
    }
  };

  return (
    <div className="mt-3 space-y-3">
      {/* Image Carousel */}
      {imageFiles.length > 0 && (
        <LessonImageCarousel 
          images={imageFiles} 
          lessonId={lesson.lesson_id} 
          token={token} 
          onDownload={handleDownload}
        />
      )}

      {/* PDF Files */}
      {pdfFiles.map((file) => (
        <div key={file.originalIndex} className="space-y-1">
          <AuthLessonPDF lessonId={lesson.lesson_id} fileIndex={file.originalIndex} token={token} fileName={file.name} />
          <Button onClick={() => handleDownload(file.originalIndex, file.name)} variant="outline" size="sm" className="text-xs">
            <Download className="h-3 w-3 mr-1" /> Изтегли PDF
          </Button>
        </div>
      ))}

      {/* HTML interactive presentations */}
      {htmlFiles.map((file) => (
        <div key={file.originalIndex} className="space-y-1">
          <AuthLessonHTML lessonId={lesson.lesson_id} fileIndex={file.originalIndex} token={token} fileName={file.name} />
          <Button onClick={() => handleDownload(file.originalIndex, file.name)} variant="outline" size="sm" className="text-xs">
            <Download className="h-3 w-3 mr-1" /> Изтегли HTML
          </Button>
        </div>
      ))}

      {/* Other Files */}
      {otherFiles.map((file) => {
        const color = getFileColor(file.name);
        return (
          <div key={file.originalIndex} className="flex items-center gap-2">
            <div className={`flex-shrink-0 w-8 h-8 ${color.bg} ${color.text} rounded flex items-center justify-center text-xs font-bold`}>
              {color.label.slice(0, 3)}
            </div>
            <Button onClick={() => handleDownload(file.originalIndex, file.name)} variant="outline" size="sm" className="flex-1 justify-start text-xs truncate">
              <Download className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{file.name}</span>
            </Button>
          </div>
        );
      })}
    </div>
  );
}
